import React, { useState } from 'react';
import { useBrand } from '@/context/BrandContext';
import { EditableText } from '../ui/EditableText';
import { Upload } from 'lucide-react';
import { ImageUploadModal } from '../image-upload';

import { BrandSectionLayout } from './BrandSectionLayout';

interface IntroductionSectionProps {
  section: any;
  isEditing: boolean;
  isGlobalDarkMode: boolean;
  editingElement: string | null;
  setEditingElement: (element: string | null) => void;
  updateSectionContent: (key: string, value: string) => void;
  updateSectionStyles: (key: string, styles: any) => void;
  pageNumber?: number;
}

export const IntroductionSection: React.FC<IntroductionSectionProps> = ({
  section,
  isEditing,
  isGlobalDarkMode,
  editingElement,
  setEditingElement,
  updateSectionContent,
  updateSectionStyles,
  pageNumber = 1
}) => {
  const { state } = useBrand();
  const [showImageModal, setShowImageModal] = useState(false);

  const handleImageSelect = (imageUrl: string) => {
    updateSectionContent('brandImage', imageUrl);
    setShowImageModal(false);
  };

  return (
    <BrandSectionLayout
      section={section}
      isEditing={isEditing}
      isGlobalDarkMode={isGlobalDarkMode}
      editingElement={editingElement}
      setEditingElement={setEditingElement}
      updateSectionContent={updateSectionContent}
      updateSectionStyles={updateSectionStyles}
      pageNumber={pageNumber}
    >
      {/* Main Content - Two-column layout with 1rem gap */}
      <div
        className="grid grid-cols-2 gap-16 h-full"
      >
        {/* Left Column - 50% width with padding and justify-between */}
        <div className="flex flex-col justify-between">
          {/* Top div - Brand Introduction Title and Description */}
          <div className="flex flex-col items-start gap-4">
            <EditableText
              value={section.content.introTitle || 'Brand Introduction'}
              onChange={(value) => updateSectionContent('introTitle', value)}
              element="h3"
              className="text-3xl leading-tight tracking-tight font-bold mb-8"
              placeholder="Brand Introduction"
              multiline={true}
              editModeEnabled={isEditing}
              isEditing={isEditing && editingElement === 'introTitle'}
              onEditStart={() => setEditingElement('introTitle')}
              onEditEnd={() => setEditingElement(null)}
              onStyleChange={(styles) => updateSectionStyles('introTitle', styles)}
              initialStyles={{
                fontFamily: state.brandData?.globalTitleFont || 'Inter',
                fontWeight: 'bold',
                ...section.content.introTitleStyles
              }}
              isGlobalDarkMode={isGlobalDarkMode}
              sectionBackgroundColor={section.customization?.backgroundColor || section.styles.backgroundColor}
            />

            <EditableText
              value={section.content.brandDescription || ''}
              onChange={(value) => updateSectionContent('brandDescription', value)}
              element="p"
              className="text-lg leading-relaxed"
              placeholder="Add Description"
              multiline={true}
              editModeEnabled={isEditing}
              isEditing={isEditing && editingElement === 'brandDescription'}
              onEditStart={() => setEditingElement('brandDescription')}
              onEditEnd={() => setEditingElement(null)}
              onStyleChange={(styles) => updateSectionStyles('brandDescription', styles)}
              initialStyles={{
                fontFamily: state.brandData?.globalTextFont || 'Inter',
                ...section.content.brandDescriptionStyles
              }}
              isGlobalDarkMode={isGlobalDarkMode}
              sectionBackgroundColor={section.customization?.backgroundColor || section.styles.backgroundColor}
            />
          </div>

        </div>

        {/* Right Column - 50% width, full height with medium border radius */}
        <div
          className="relative group h-full"
        >
          {/* Image Container with medium border radius (0.375rem/6px) */}
          <div
            className="w-full h-full relative rounded-md overflow-hidden"
            style={{
              borderRadius: '0.375rem' // 6px medium border radius
            }}
          >
            {/* Main Image with full container fill and medium border radius */}
            <img
              src={section.content.brandImage || '/img-placeholder-16-9.png'}
              alt="Brand Visual"
              className="w-full h-full rounded-md"
              style={{
                width: '100%',
                height: '100%',
                margin: 0,
                padding: 0,
                border: 'none',
                borderRadius: '0.375rem', // 6px medium border radius
                objectFit: 'cover',
                display: 'block'
              }}
              onError={(e) => {
                // Fallback to default image if custom image fails
                e.currentTarget.src = '/img-placeholder-16-9.png';
              }}

            />

            {/* Upload Overlay - Only visible in edit mode */}
            {isEditing && (
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center rounded-md">
                <div className="text-center text-white">
                  <Upload className="w-8 h-8 mx-auto mb-2" />
                  <p className="text-sm font-medium mb-2">Change Image</p>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowImageModal(true);
                    }}
                    className="px-3 py-2 bg-white/90 text-black rounded text-sm hover:bg-white transition-colors"
                  >
                    Upload New Image
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Image Upload Modal */}
      <ImageUploadModal
        isOpen={showImageModal}
        onClose={() => setShowImageModal(false)}
        onImageSelect={handleImageSelect}
        isGlobalDarkMode={isGlobalDarkMode}
        title="Upload Brand Introduction Image"
        acceptedFormats={['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg']}
        maxFileSize={10}
        showUnsplash={true}
        initialSearchQuery="business professional"
      />
    </BrandSectionLayout>
  );
};
